import { useCallback, useEffect, useMemo } from "react"
import { useController, useFormContext } from "react-hook-form"
import { useIntl } from "react-intl"

import { formMessages } from "src/common/form.messages"
import type { MonthpickerProps } from "./Monthpicker"
import { Monthpicker } from "./Monthpicker"

export type MonthPickerFieldProps = Omit<MonthpickerProps, "value" | "language" | "dateFormat">

export const MonthpickerField = (props: MonthPickerFieldProps) => {
	const { clearable, clearButtonLabel, disabledHint, label, maxDate, minDate, name, required, state, ...rest } = props
	const { formatMessage } = useIntl()

	const { control, trigger } = useFormContext()

	const rules = useMemo(() => {
		return {
			required: {
				message: formatMessage(formMessages.required),
				value: !!required,
			},
		}
	}, [formatMessage, required])

	const {
		field: { onChange, ref, value },
		fieldState: { error, isDirty },
		formState: { isSubmitted },
	} = useController({
		control,
		name,
		rules,
		shouldUnregister: true,
	})

	const handleChange = useCallback(
		(newValue: { input: string; isoDate: string | null; isValid: boolean }) => {
			if (!value && newValue.isoDate === null) {
				return
			}
			if (newValue.isoDate) {
				const date = new Date(newValue.isoDate)
				const yearMonth = {
					month: date.getMonth() + 1,
					year: date.getFullYear(),
				}
				onChange(yearMonth)
			} else {
				onChange(null)
			}
		},
		[onChange, value],
	)

	// Fields need to retrigger validation in order to apply changed rules, since the rules are cached inside react-hook-form.
	// See https://github.com/react-hook-form/react-hook-form/issues/1749 and https://github.com/react-hook-form/react-hook-form/issues/8310
	useEffect(() => {
		if (isDirty || isSubmitted) {
			trigger(name)
		}
	}, [rules, trigger, name, isDirty, isSubmitted])

	return (
		<Monthpicker
			{...rest}
			name={name}
			label={label}
			ref={ref}
			value={value}
			minDate={minDate}
			maxDate={maxDate}
			disabledHint={disabledHint}
			required={!!required}
			clearable={clearable}
			clearButtonLabel={clearButtonLabel}
			state={error ? "error" : state}
			message={error?.message}
			onChange={handleChange}
		/>
	)
}
